import React, { useMemo } from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import { formatMinutesAsHours } from './format'
import { useStyles } from './styles'
import { ParkingDonutSlice } from './types'

/** Figma CategoríasGráficos */
const DONUT_COLORS = ['#E0081A', '#FF3647', '#FF6F7B', '#FF9AA3', '#FFC2C7', '#E4E4E7', '#C8C8C8']

interface Props {
  title: string
  periodLabel?: string
  slices: ParkingDonutSlice[]
  isLoading?: boolean
}

interface TooltipPayloadItem {
  payload?: ParkingDonutSlice & { fill?: string }
  name?: string
  value?: number
  color?: string
}

interface DonutTooltipProps {
  active?: boolean
  payload?: TooltipPayloadItem[]
  classes: ReturnType<typeof useStyles>
}

const DonutTooltipContent: React.FC<DonutTooltipProps> = ({ active, payload, classes }) => {
  if (!active || !payload?.length) return null

  const entry = payload[0]
  const slice = entry.payload
  if (!slice) return null

  const color = entry.color || slice.fill || DONUT_COLORS[0]

  return (
    <div className={classes.donutTooltip}>
      <span className={classes.donutTooltipBar} style={{ backgroundColor: color }} />
      <div className={classes.donutTooltipCopy}>
        <span className={classes.donutTooltipValue}>
          <span className={classes.donutLegendHours}>{formatMinutesAsHours(slice.value)}</span>{' '}
          <span className={classes.donutLegendPercent}>({slice.percent}%)</span>
        </span>
        <span className={classes.donutTooltipName}>{slice.name}</span>
      </div>
    </div>
  )
}

const HoursDonutCard: React.FC<Props> = ({ title, periodLabel, slices, isLoading }) => {
  const classes = useStyles()

  const chartData = useMemo(() => {
    const withValue = slices.filter(slice => (slice.value || 0) > 0)
    const total = withValue.reduce((sum, slice) => sum + (slice.value || 0), 0)
    return withValue.map(slice => ({
      ...slice,
      percent: total > 0 ? Math.round(((slice.value || 0) / total) * 100) : 0,
    }))
  }, [slices])

  const totalMinutes = useMemo(
    () => chartData.reduce((sum, slice) => sum + (slice.value || 0), 0),
    [chartData],
  )

  return (
    <div className={classes.donutCard}>
      <div className={classes.donutInner}>
        <div className={classes.donutHeader}>
          <div className={classes.donutHeaderText}>
            <h3 className={classes.donutTitle}>{title}</h3>
            {periodLabel ? <div className={classes.donutPeriod}>{periodLabel}</div> : null}
          </div>
        </div>

        {isLoading ? (
          <div className={classes.donutLoading}>
            <CircularProgress size={28} />
          </div>
        ) : chartData.length === 0 ? (
          <div className={classes.donutEmpty}>Sin datos en el período</div>
        ) : (
          <>
            <div className={classes.donutChartWrap}>
              <div className={classes.donutPie}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={58}
                      outerRadius={96}
                      paddingAngle={0}
                      stroke="none"
                    >
                      {chartData.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={DONUT_COLORS[index % DONUT_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      content={<DonutTooltipContent classes={classes} />}
                      cursor={false}
                      wrapperStyle={{ zIndex: 20, outline: 'none' }}
                      allowEscapeViewBox={{ x: true, y: true }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className={classes.donutCenter}>
                <span className={classes.donutCenterValue}>{formatMinutesAsHours(totalMinutes)}</span>
              </div>
            </div>

            <ul className={classes.donutLegend}>
              {chartData.map((slice, index) => (
                <li key={`${slice.name}-${index}`} className={classes.donutLegendItem}>
                  <span
                    className={classes.donutLegendBar}
                    style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }}
                  />
                  <div className={classes.donutLegendCopy}>
                    <span className={classes.donutLegendValue}>
                      <span className={classes.donutLegendHours}>
                        {formatMinutesAsHours(slice.value)}
                      </span>{' '}
                      <span className={classes.donutLegendPercent}>({slice.percent}%)</span>
                    </span>
                    <span className={classes.donutLegendName} title={slice.name}>
                      {slice.name}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

export { HoursDonutCard }
